import { useEffect, useState } from 'react';
import { useFormV2 } from '@abyss/web/hooks/useForm';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { PageScrollService } from '@src/api/common/PageScrollService';
import { AlertService } from '@src/api/common/AlertService';
import { RedirectService } from '@src/api/common/RedirectService';
import { ApiStatus } from '@src/constants/ApiStatus';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { TechnologyService } from '@src/api/web-services/features/TechnologyService';
import { ActionService } from '@src/api/web-services/features/ActionService';
import { EmployeeService } from '@src/api/web-services/features/EmployeeService';
import { ResponseService } from '@src/api/web-services/features/ResponseService';
import { ThemeService } from '@src/api/web-services/features/ThemeService';
import { FollowUpStatusService } from '@src/api/web-services/features/admin/FollowUpStatusService';
import { ApiToInputDataTransformService } from '@src/api/common/ApiToInputDataTransformService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { Breadcrumbs, Title } from './PageInfo';
import { useDateField } from '@src/api/common/useDateField';
import { UrlService } from '@src/api/common/UrlService';
import { UserService } from '@src/api/common/UserService';

const ResponseDetailController = () => {
  type Data = {
    item: any;
    detailResponse: any;
    isEdit: boolean;
    technologies: Array<any>;
    themes: Array<any>;
    actions: Array<any>;
    actionOwners: Array<any>;
    assignedTos: Array<any>;
    followUpStatuses: Array<any>;
  };

  const [pageData, setPageData] = useState<Data>({
    item: {},
    detailResponse: null,
    isEdit: false,
    technologies: [],
    themes: [],
    actions: [],
    actionOwners: [],
    assignedTos: [],
    followUpStatuses: [],
  });
  const { UpdateBreadcrumb } = BreadcrumbService();
  const { UpdatePageTitle } = PageTitleService();
  const { GetSelectInputOptions } = ApiToInputDataTransformService();
  const [fieldsDisabled, setFieldsDisabled] = useState<boolean>(false);
  const { getRouteParams } = useRouter();
  const params = getRouteParams('');
  const { ScrollToTop, pageTop } = PageScrollService();
  const { AlertModel, ShowAlert } = AlertService();
  const { Redirect, RedirectDuration } = RedirectService();

  const {
    GetResponseAll,
    GetResponseBySourceId,
    LogicDeleteResponseById,
    CreateOrUpdateResponse,
  } = ResponseService();
  const { GetAll: GetAllTechnology } = TechnologyService();
  const { GetAll: GetAllAction } = ActionService();
  const { GetAll: GetAllEmployee } = EmployeeService();
  const { GetAll: GetAllTheme } = ThemeService();
  const { GetAll: GetAllFollowUpStatus } = FollowUpStatusService();

  const form = useFormV2({
    defaultValues: {
      id: 0,
      name: null,
      followUpDate: '',
    },
    values: pageData.item,
  });

  const followUpDateField = useDateField(form, 'followUpDate');

  const DataSource = {
    GoBackToList: () => {
      Redirect(`/responses`, RedirectDuration.SHORT);
    },
    GetFormattedApiData: (data: any): any => {
      data.followUpDate = followUpDateField.fromApiDate(data.followUpDate);
      const answers = (data.responseText ?? '')
        .split('|')
        .map((s: string) => s.trim());
      data.question1 = answers[0] ?? '';
      data.question2 = answers[1] ?? '';
      data.question3 = answers[2] ?? '';
      data.question4 = answers[3] ?? '';
      data.question5 = answers[4] ?? '';
      return data;
    },
    GetDetail: async () => {
      const body = {
        sourceSessionId: params?.id ?? 0,
      };
      const apiData = await GetResponseBySourceId(body);
      await setPageData((pageData) => ({
        ...pageData,
        detailResponse: apiData,
      }));
      if (apiData.status === ApiStatus.Success && apiData.data?.result) {
        let data = apiData.data.result;
        data = DataSource.GetFormattedApiData(data);
        setPageData((p) => ({
          ...p,
          item: data,
        }));
      } else if (apiData.data?.message) {
        AlertModel.variant = AlertVariantType.ERROR;
        AlertModel.message = apiData.data?.message;
        AlertModel.visible = true;
        await ShowAlert(AlertModel);
      }
    },
    GetTechnologies: async () => {
      const body = { isActive: true };
      const query = UrlService.BuildQuery(body);
      const transformedData = await GetSelectInputOptions(
        'id',
        'name',
        GetAllTechnology,
        query
      );
      await setPageData((p) => ({
        ...p,
        technologies: [...transformedData.data].sort((a, b) =>
          a.label.localeCompare(b.label)
        ),
      }));
    },
    GetThemes: async () => {
      const body = { isActive: true };
      const query = UrlService.BuildQuery(body);
      const transformedData = await GetSelectInputOptions(
        'id',
        'name',
        GetAllTheme,
        query
      );
      await setPageData((p) => ({
        ...p,
        themes: [...transformedData.data].sort((a, b) =>
          a.label.localeCompare(b.label)
        ),
      }));
    },
    GetActions: async () => {
      const body = { isActive: true };
      const query = UrlService.BuildQuery(body);
      const transformedData = await GetSelectInputOptions(
        'id',
        'name',
        GetAllAction,
        query
      );
      await setPageData((p) => ({
        ...p,
        actions: [...transformedData.data].sort((a, b) =>
          a.label.localeCompare(b.label)
        ),
      }));
    },
    GetEmployees: async () => {
      const body = { isActive: true };
      const query = UrlService.BuildQuery(body);
      const transformedData = await GetSelectInputOptions(
        'id',
        'displayName',
        GetAllEmployee,
        query
      );
      const sortedEmployees = [...transformedData.data].sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      await setPageData((p) => ({
        ...p,
        actionOwners: sortedEmployees,
        assignedTos: sortedEmployees,
      }));
    },
    GetFollowUpStatuses: async () => {
      const body = { isActive: true };
      const query = UrlService.BuildQuery(body);
      const transformedData = await GetSelectInputOptions(
        'id',
        'name',
        GetAllFollowUpStatus,
        query
      );
      await setPageData((p) => ({
        ...p,
        followUpStatuses: [...transformedData.data].sort((a, b) =>
          a.label.localeCompare(b.label)
        ),
      }));
    },

    DeleteItem: async () => {
      if (params?.id !== null) {
        const body = {
          sourceSessionId: params?.id ?? 0,
        };
        const apiData = await LogicDeleteResponseById(body);

        if (apiData.data?.message) {
          AlertModel.variant =
            apiData.status === ApiStatus.Success
              ? AlertVariantType.SUCCESS
              : AlertVariantType.ERROR;
          AlertModel.message = apiData.data?.message;
          AlertModel.visible = true;
          await ShowAlert(AlertModel);
          if (apiData?.status === ApiStatus.Success) {
            form.reset();
            DataSource.GoBackToList();
          }
        }
      }
    },
    PostDetail: async () => {
      if (followUpDateField.hasInvalidDate()) {
        form.setError('followUpDate', {
          type: 'manual',
          message: 'Please enter a valid date',
        });
        return false;
      }
      const body = form.getValues();
      const response = {
        id: body.id ?? 0,
        sourceSessionId: params?.id ?? 0,
        themeId: body.themeId || null,
        technologyId: body.technologyId || null,
        followUpStatusId: body.followUpStatusId || null,
        assignedToId: body.assignedToId || null,
        actionId: body.actionId || null,
        actionOwnerId: body.actionOwnerId || null,
        themeNotes: body.themeNotes || null,
        actionNotes: body.actionNotes || null,
        followUpMessage: body.followUpMessage || null,
        followUpDate: followUpDateField.toApiDate(body.followUpDate),
      };
      const apiData = await CreateOrUpdateResponse(response);
      if (
        (apiData?.status === ApiStatus.Success ||
          apiData?.status === ApiStatus.Created) &&
        apiData?.data?.result
      ) {
        let data = apiData.data.result;
        data = DataSource.GetFormattedApiData(data);
        await setPageData((p) => ({
          ...p,
          item: data,
        }));
        if (data.id > 0) {
          DataSource.GoBackToList();
        }
      }
      if (apiData.data?.message) {
        AlertModel.variant =
          apiData.status === ApiStatus.Success ||
          apiData?.status === ApiStatus.Created
            ? AlertVariantType.SUCCESS
            : AlertVariantType.ERROR;
        AlertModel.message = apiData.data?.message;
        AlertModel.visible = true;
        await ShowAlert(AlertModel);
      }
      return true;
    },
  };

  const Action = {
    Init: async () => {
      await UpdateBreadcrumb(Breadcrumbs);
      await UpdatePageTitle(Title);
      const userData = UserService.GetUser();
      if (userData?.allowedRoles?.includes('AZU_CTE_VIEWER')) {
        setFieldsDisabled(true);
      }
      await Action.Load();
    },
    Load: async () => {
      await Promise.all([
        DataSource.GetDetail(),
        DataSource.GetTechnologies(),
        DataSource.GetThemes(),
        DataSource.GetActions(),
        DataSource.GetEmployees(),
        DataSource.GetFollowUpStatuses(),
      ]);
    },
    OnDeleteConfirm: async () => {
      await DataSource.DeleteItem();
      await ScrollToTop();
    },
    Save: async () => {
      if (fieldsDisabled) return;
      const saved = await DataSource.PostDetail();
      if (saved) await ScrollToTop();
    },

    OnDateChange: followUpDateField.onChange,
  };

  useEffect(() => {
    if (params?.id !== null) {
      setPageData((p) => ({
        ...p,
        item: {
          id: params.id,
        },
        isEdit: Number(params.id) > 0 ? true : false,
      }));

      Action.Init();
    }
  }, []);

  return {
    form,
    Action,
    pageTop,
    params,
    pageData,
    fieldsDisabled,
  };
};

export { ResponseDetailController };
